import React, { useEffect, useState } from 'react';
import { User, Upload, FileText, X } from 'lucide-react';
import AWS from 'aws-sdk';

const API_ENDPOINT = process.env.REACT_APP_API_URL;

// New dedicated table for waiting list notification recipients
const WAITING_LIST_TABLE = 'waiting-list-recipients';

function Settings() {
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('emails'); // 'emails' | 'tenant' | 'waiting' | 'myob' | 'admin'

  // Configure AWS for S3 (for tenant information file)
  AWS.config.update({
    region: process.env.REACT_APP_AWS_REGION,
    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY
  });
  const s3 = new AWS.S3();
  const dynamoDb = new AWS.DynamoDB.DocumentClient();
  const TENANT_PREFIX = 'tenant-information/';
  const TENANT_BUCKET = 'storehere-agreements';

  const [tenantFileUrl, setTenantFileUrl] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(false);
  const [tenantUploading, setTenantUploading] = useState(false);
  const [tenantFile, setTenantFile] = useState(null);

  useEffect(() => { loadTemplates(); }, []);
  useEffect(() => { if (activeTab === 'tenant') loadTenantInfo(); }, [activeTab]);

  const authHeader = async () => {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session?.tokens?.idToken?.toString() || session?.tokens?.accessToken?.toString();
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch (_) {
      return {};
    }
  };

  const parseApiResponse = async (res) => {
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const unwrap = (obj) => {
      let cur = obj;
      let depth = 0;
      while (depth < 3 && cur && !cur.templates) {
        if (!cur.body) break;
        if (typeof cur.body === 'string') {
          let s = cur.body;
          try {
            if (cur.isBase64Encoded) {
              // decode base64 to string (browser)
              s = atob(s);
            }
          } catch (_) {}
          try {
            cur = JSON.parse(s);
          } catch (_) {
            break;
          }
        } else if (typeof cur.body === 'object') {
          cur = cur.body;
        } else {
          break;
        }
        depth++;
      }
      return cur;
    };

    let text = await res.text();
    let obj;
    try { obj = JSON.parse(text); } catch { obj = {}; }
    const cur = unwrap(obj);
    if (cur && cur.templates) return cur;
    throw new Error('Invalid response');
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setMessage('');
      const res = await fetch(`${API_ENDPOINT}/email-templates`, {
        method: 'GET',
        headers: await authHeader()
      });
      const data = await parseApiResponse(res);
      setTemplates(data.templates);
    } catch (err) {
      console.error('Error loading templates:', err);
      setMessage('Error loading templates');
    } finally {
      setLoading(false);
    }
  };

  const loadTenantInfo = async () => {
    try {
      setTenantLoading(true);
      setTenantFileUrl(null);
      const key = await listTenantInfo(s3, TENANT_BUCKET, TENANT_PREFIX);
      if (key) {
        const url = await s3.getSignedUrlPromise('getObject', { Bucket: TENANT_BUCKET, Key: key, Expires: 3600 });
        setTenantFileUrl(url);
      }
    } catch (e) {
      console.error('Error loading tenant info:', e);
    } finally {
      setTenantLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="bg-orange-100 rounded-lg p-3">
              <User className="text-orange-600" size={32} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Settings</h1>
              <p className="text-gray-600">Manage admin settings</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex gap-6" aria-label="Tabs">
            <button
              className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium ${activeTab === 'emails' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              onClick={() => setActiveTab('emails')}
            >
              Edit Emails
            </button>
            <button
              className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium ${activeTab === 'waiting' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              onClick={() => setActiveTab('waiting')}
            >
              Waiting List Emails
            </button>
            <button
              className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium ${activeTab === 'tenant' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              onClick={() => setActiveTab('tenant')}
            >
              Tenant Information
            </button>
          <button
            className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium ${activeTab === 'myob' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            onClick={() => setActiveTab('myob')}
          >
            MYOB Recipients
          </button>
          <button
            className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium ${activeTab === 'admin' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            onClick={() => setActiveTab('admin')}
          >
            Admin
          </button>
          </nav>
        </div>

        {activeTab === 'emails' && (
        <EmailTemplatesEditor
          templates={templates}
          loading={loading}
          saving={saving}
          message={message}
          onReload={loadTemplates}
          onSave={async (payload) => {
            try {
              setSaving(true);
              setMessage('');
              const res = await fetch(`${API_ENDPOINT}/email-templates`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
                body: JSON.stringify(payload)
              });
              const data = await parseApiResponse(res);
              setTemplates(data.templates);
              setMessage('Saved successfully');
              setTimeout(() => setMessage(''), 2000);
            } catch (e) {
              console.error('Save template error:', e);
              setMessage('Error saving template');
            } finally {
              setSaving(false);
            }
          }}
        />)}

        {activeTab === 'tenant' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Tenant Information</h2>
            {tenantLoading ? (
              <div className="text-gray-600">Loading tenant information...</div>
            ) : tenantFileUrl ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-gray-700">
                  <FileText size={18} />
                  <a href={tenantFileUrl} target="_blank" rel="noreferrer" className="text-orange-600 hover:text-orange-700 underline">
                    View current file
                  </a>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <input type="file" onChange={(e) => setTenantFile(e.target.files?.[0] || null)} />
                    {tenantFile && (
                      <button onClick={() => setTenantFile(null)} className="text-gray-500 hover:text-gray-700" title="Clear">
                        <X size={18} />
                      </button>
                    )}
                  </div>
                  <button
                    onClick={async () => {
                      if (!tenantFile) return;
                      try {
                        setTenantUploading(true);
                        // Convert file to base64
                        const base64Data = await new Promise((resolve) => {
                          const reader = new FileReader();
                          reader.onloadend = () => resolve(reader.result.split(',')[1]);
                          reader.readAsDataURL(tenantFile);
                        });
                        const res = await fetch(`${API_ENDPOINT}/upload-tenant-info`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
                          body: JSON.stringify({ fileData: base64Data, fileName: tenantFile.name })
                        });
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        await loadTenantInfo();
                        setTenantFile(null);
                      } catch (err) {
                        console.error('Tenant info upload error:', err);
                        setMessage('Error uploading tenant information');
                      } finally {
                        setTenantUploading(false);
                      }
                    }}
                    disabled={!tenantFile || tenantUploading}
                    className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white px-4 py-2 rounded-lg"
                  >
                    {tenantUploading ? 'Uploading...' : 'Replace File'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-600">No tenant information file uploaded yet.</p>
                <div className="flex items-center gap-3">
                  <Upload size={18} />
                  <input type="file" onChange={(e) => setTenantFile(e.target.files?.[0] || null)} />
                </div>
                <button
                  onClick={async () => {
                    if (!tenantFile) return;
                    try {
                      setTenantUploading(true);
                      const base64Data = await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result.split(',')[1]);
                        reader.readAsDataURL(tenantFile);
                      });
                      const res = await fetch(`${API_ENDPOINT}/upload-tenant-info`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
                        body: JSON.stringify({ fileData: base64Data, fileName: tenantFile.name })
                      });
                      if (!res.ok) throw new Error(`HTTP ${res.status}`);
                      await loadTenantInfo();
                      setTenantFile(null);
                    } catch (err) {
                      console.error('Tenant info upload error:', err);
                      setMessage('Error uploading tenant information');
                    } finally {
                      setTenantUploading(false);
                    }
                  }}
                  disabled={!tenantFile || tenantUploading}
                  className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white px-4 py-2 rounded-lg"
                >
                  {tenantUploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'waiting' && (
          <WaitingListEmailsTab dynamoDb={dynamoDb} />
        )}

        {activeTab === 'myob' && (
          <MyobRecipientsTab />
        )}
        {activeTab === 'admin' && (
          <AdminUsersTab />
        )}
      </div>
    </div>
  );
}

function EmailTemplatesEditor({ templates, loading, saving, message, onReload, onSave }) {
  const [selectedKey, setSelectedKey] = useState('welcome');
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  const [cc, setCc] = useState('');

  const keys = [
    { key: 'welcome', label: 'Welcome Email' },
    { key: 'payment_confirmation', label: 'Payment Confirmation' },
    { key: 'payment_failure', label: 'Payment Failure' },
    { key: 'reminder_24h', label: '24 Hour Reminder' },
    { key: 'reminder_3d', label: '2 Day Reminder' },
    { key: 'reminder_6d', label: '7 Day Reminder' }
  ];

  useEffect(() => {
    const tpl = templates?.[selectedKey] || { subject: '', text: '', html: '', cc: '' };
    setSubject(tpl.subject || '');
    setText(tpl.text || tpl.html || '');
    setCc(tpl.cc || '');
  }, [templates, selectedKey]);

  const placeholderHelp = (
    <div className="text-xs text-gray-500">
      Placeholders: {'{{container_number}} {{site_id}} {{invoice_url}} {{amount}}'}
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="border-b border-gray-200 bg-gray-50 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-gray-800">Edit Emails</h2>
          <select
            className="ml-3 text-sm border border-gray-300 rounded px-2 py-1"
            value={selectedKey}
            onChange={(e) => setSelectedKey(e.target.value)}
          >
            {keys.map(k => (
              <option key={k.key} value={k.key}>{k.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onReload} className="text-sm px-3 py-1 border rounded hover:bg-gray-50">Reload</button>
          {message && <span className="text-sm text-gray-500">{message}</span>}
          <button
            onClick={() => onSave({ key: selectedKey, subject, text, ...(selectedKey === 'reminder_6d' ? { cc } : {}) })}
            disabled={saving}
            className="text-sm bg-orange-500 text-white px-3 py-1 rounded hover:bg-orange-600 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
      <div className="p-6 space-y-4">
        {loading && <div className="text-gray-600">Loading templates...</div>}
        {!loading && (
          <>
            {selectedKey === 'reminder_6d' && (
              <div>
                <label className="text-sm font-medium text-gray-700">CC</label>
                <input
                  type="text"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                  placeholder="Comma-separated emails (e.g., a@b.com, c@d.com)"
                />
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-gray-700">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
                placeholder="Subject"
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Body</label>
              {placeholderHelp}
            </div>
            <textarea
              rows={14}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full font-mono text-sm px-3 py-2 border border-gray-300 rounded"
              placeholder="Email body (plain text)"
            />
          </>
        )}
      </div>
    </div>
  );
}

export default Settings;

function WaitingListEmailsTab() {
  const [loading, setLoading] = useState(false);
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [msg, setMsg] = useState('');

  const authHeader = async () => {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session?.tokens?.idToken?.toString() || session?.tokens?.accessToken?.toString();
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch (_) {
      return {};
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_ENDPOINT}/waiting-list-recipients`, { headers: await authHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      let text = await res.text();
      let obj; try { obj = JSON.parse(text); } catch { obj = {}; }
      const data = obj && obj.body ? (() => { try { return JSON.parse(obj.body); } catch { return obj; } })() : obj;
      const list = (data?.recipients || []).slice().sort((a, b) => a.localeCompare(b));
      setEmails(list);
    } catch (e) {
      console.error('Load waiting list emails error:', e);
      setMsg('Error loading emails');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    const email = (newEmail || '').trim().toLowerCase();
    if (!email) return;
    try {
      setMsg('');
      const res = await fetch(`${API_ENDPOINT}/waiting-list-recipients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
        body: JSON.stringify({ email })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setNewEmail('');
      await load();
    } catch (e) {
      console.error('Add email error:', e);
      setMsg('Error adding email');
    }
  };

  const remove = async (email) => {
    try {
      setMsg('');
      const res = await fetch(`${API_ENDPOINT}/waiting-list-recipients`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
        body: JSON.stringify({ email })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) {
      console.error('Remove email error:', e);
      setMsg('Error removing email');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">Waiting List Emails</h2>
      <p className="text-sm text-gray-600 mb-4">These addresses receive an email when a customer submits the waiting list form on the homepage.</p>
      <div className="flex gap-2 mb-4">
        <input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="name@example.com"
          className="flex-1 px-3 py-2 border border-gray-300 rounded"
        />
        <button onClick={add} className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded">Add</button>
      </div>
      {msg && <div className="text-sm text-gray-600 mb-3">{msg}</div>}
      {loading ? (
        <div className="text-gray-600">Loading...</div>
      ) : emails.length === 0 ? (
        <div className="text-gray-600">No emails configured.</div>
      ) : (
        <ul className="divide-y divide-gray-200">
          {emails.map(e => (
            <li key={e} className="flex items-center justify-between py-2">
              <span className="text-gray-800">{e}</span>
              <button onClick={() => remove(e)} className="text-sm text-red-600 hover:text-red-700">Remove</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Helpers
async function listTenantInfo(s3, bucket, prefix) {
  const res = await s3.listObjectsV2({ Bucket: bucket, Prefix: prefix }).promise();
  const items = res.Contents || [];
  if (!items.length) return null;
  items.sort((a, b) => new Date(b.LastModified) - new Date(a.LastModified));
  return items[0].Key;
}

function MyobRecipientsTab() {
  const [loading, setLoading] = useState(false);
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [msg, setMsg] = useState('');

  const authHeader = async () => {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session?.tokens?.idToken?.toString() || session?.tokens?.accessToken?.toString();
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch (_) { return {}; }
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_ENDPOINT}/export-recipients`, { headers: await authHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = (data?.recipients || []).slice().sort((a,b)=>a.localeCompare(b));
      setEmails(list);
    } catch (e) {
      console.error('Load export recipients error:', e);
      setMsg('Error loading emails');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    const email = (newEmail || '').trim().toLowerCase();
    if (!email) return;
    try {
      setMsg('');
      const res = await fetch(`${API_ENDPOINT}/export-recipients`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
        body: JSON.stringify({ email })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setNewEmail('');
      await load();
    } catch (e) { console.error('Add email error:', e); setMsg('Error adding email'); }
  };

  const remove = async (email) => {
    try {
      setMsg('');
      const res = await fetch(`${API_ENDPOINT}/export-recipients`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
        body: JSON.stringify({ email })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) { console.error('Remove email error:', e); setMsg('Error removing email'); }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">MYOB Export Recipients</h2>
      <p className="text-sm text-gray-600 mb-4">These addresses receive the monthly MYOB CSV export automatically.</p>
      <div className="flex gap-2 mb-4">
        <input type="email" value={newEmail} onChange={(e)=>setNewEmail(e.target.value)} placeholder="name@example.com" className="flex-1 px-3 py-2 border border-gray-300 rounded" />
        <button onClick={add} className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded">Add</button>
      </div>
      {msg && <div className="text-sm text-gray-600 mb-3">{msg}</div>}
      {loading ? (
        <div className="text-gray-600">Loading...</div>
      ) : emails.length === 0 ? (
        <div className="text-gray-600">No emails configured.</div>
      ) : (
        <ul className="divide-y divide-gray-200">
          {emails.map(e => (
            <li key={e} className="flex items-center justify-between py-2">
              <span className="text-gray-800">{e}</span>
              <button onClick={() => remove(e)} className="text-sm text-red-600 hover:text-red-700">Remove</button>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-gray-500 mt-4">Exports run automatically at the start of each month for the previous month (Australia/Sydney).</p>
    </div>
  );
}

function AdminUsersTab() {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [msg, setMsg] = useState('');

  const authHeader = async () => {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session?.tokens?.idToken?.toString() || session?.tokens?.accessToken?.toString();
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch (_) { return {}; }
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_ENDPOINT}/admin-users`, { headers: await authHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setUsers((data?.users || []).slice());
    } catch (e) {
      console.error('Load admins error:', e);
      setMsg('Error loading admins');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    const em = (email || '').trim().toLowerCase();
    if (!em) return;
    try {
      setMsg('');
      const res = await fetch(`${API_ENDPOINT}/admin-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
        body: JSON.stringify({ email: em, firstName: firstName.trim(), lastName: lastName.trim(), phone: phone.trim() })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      try {
        const data = await res.json();
        if (data?.invited && data?.existingUser) setMsg('Admin added to group. Reset email sent.');
        else if (data?.invited) setMsg('Admin added. Invite email with temporary password sent.');
        else setMsg('Admin added.');
      } catch (_) { setMsg('Admin added.'); }
      setEmail(''); setFirstName(''); setLastName(''); setPhone('');
      await load();
    } catch (e) { console.error('Add admin error:', e); setMsg('Error adding admin'); }
  };

  const remove = async (username) => {
    try {
      setMsg('');
      const res = await fetch(`${API_ENDPOINT}/admin-users`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
        body: JSON.stringify({ username })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) { console.error('Delete admin error:', e); setMsg('Error deleting admin'); }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">Admin Users</h2>
      <p className="text-sm text-gray-600 mb-4">Admins are regular users who are also in the <span className="font-mono">Admins</span> Cognito group. Creating an admin registers the email in Cognito and adds it to the group. A password reset/invite is still required.</p>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-3">
        <div className="md:col-span-3">
          <input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="Email" className="w-full px-4 py-3 text-base border border-gray-300 rounded" />
        </div>
        <div className="md:col-span-1">
          <input type="text" value={firstName} onChange={(e)=>setFirstName(e.target.value)} placeholder="First name" className="w-full px-4 py-3 text-base border border-gray-300 rounded" />
        </div>
        <div className="md:col-span-1">
          <input type="text" value={lastName} onChange={(e)=>setLastName(e.target.value)} placeholder="Last name" className="w-full px-4 py-3 text-base border border-gray-300 rounded" />
        </div>
        <div className="md:col-span-1">
          <input type="tel" value={phone} onChange={(e)=>setPhone(e.target.value)} placeholder="Phone" className="w-full px-4 py-3 text-base border border-gray-300 rounded" />
        </div>
      </div>
      <div className="mb-6">
        <button onClick={add} className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded">Add Admin</button>
      </div>

      {msg && <div className="text-sm text-gray-600 mb-3">{msg}</div>}
      {loading ? (
        <div className="text-gray-600">Loading...</div>
      ) : users.length === 0 ? (
        <div className="text-gray-600">No admins found.</div>
      ) : (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-gray-600 border-b">
              <th className="py-2 pr-2">Email</th>
              <th className="py-2 pr-2">Status</th>
              <th className="py-2 pr-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.username} className="border-b last:border-b-0">
                <td className="py-2 pr-2">{u.email || '-'}</td>
                <td className="py-2 pr-2">{u.status}{u.enabled === false ? ' (disabled)' : ''}</td>
                <td className="py-2 pr-2">
                  <button onClick={()=>remove(u.username)} className="text-red-600 hover:text-red-700">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="text-xs text-gray-500 mt-4">Note: Deleting here removes the Cognito user entirely.</p>
    </div>
  );
}
